import debounce from 'lodash.debounce';
import { computed, getCurrentScope, isRef, ref, shallowRef, watch } from 'vue';
import { isCancelablePromise } from '@nzyme/utils/Cancelable.js';
import { makeRef } from './reactivity/makeRef.js';
import { reactive } from './reactivity/reactive.js';
/**
 * Creates a reactive data source that loads and manages data from an API or other source
 */
export function useDataSource(opts) {
    const behavior = opts.behavior;
    const loadRef = shallowRef(behavior !== 'lazy');
    const defaultRef = makeRef(opts.default);
    const dataRef = isRef(opts.data) ? opts.data : shallowRef();
    const dataCallback = isRef(opts.data) ? null : opts.data;
    const paramsRef = makeRef(opts.params);
    const dirtyRef = ref(false);
    const loadedRef = ref(false);
    const effectScope = getCurrentScope();
    const pendingRef = ref(null);
    const errorRef = shallowRef(null);
    const debounceOptions = getDebounceOptions(opts.debounce);
    const debouncedLoad = debounceOptions ? debounce(loadData, debounceOptions.time, debounceOptions) : loadData;
    const value = computed({
        get: () => {
            loadRef.value = true;
            const data = dataRef.value;
            if (data === undefined) {
                return defaultRef.value;
            }
            return data;
        },
        set: (newValue) => {
            dataRef.value = newValue;
        },
    });
    watch(paramsRef, debouncedLoad, { deep: true, immediate: behavior === 'eager' });
    if (behavior === 'lazy') {
        watch(loadRef, () => {
            if (!pendingRef.value) {
                void debouncedLoad();
            }
        });
    }
    const dataSource = reactive({
        value,
        pending: computed(() => pendingRef.value),
        loaded: computed(() => loadedRef.value),
        error: computed(() => errorRef.value),
        get,
        reload,
        clear,
        invalidate,
    });
    return dataSource;
    async function get() {
        loadRef.value = true;
        const pending = pendingRef.value;
        if (pending) {
            return await pending;
        }
        if (dataRef.value !== undefined && !dirtyRef.value) {
            return dataRef.value;
        }
        return await reload();
    }
    async function reload() {
        loadRef.value = true;
        if ('flush' in debouncedLoad) {
            void debouncedLoad();
            return (await debouncedLoad.flush());
        }
        return (await debouncedLoad());
    }
    function clear() {
        const pending = pendingRef.value;
        if (pending && isCancelablePromise(pending)) {
            pending.cancel();
        }
        pendingRef.value = null;
        dataRef.value = undefined;
        errorRef.value = null;
        if (behavior === 'lazy') {
            loadRef.value = false;
        }
    }
    function invalidate() {
        if (behavior === 'lazy') {
            loadRef.value = false;
        }
        dirtyRef.value = true;
    }
    // function used to load the data
    async function loadData() {
        if (!loadRef.value) {
            return undefined;
        }
        const pending = pendingRef.value;
        if (pending && isCancelablePromise(pending)) {
            pending.cancel();
        }
        pendingRef.value = null;
        const params = paramsRef.value;
        let promise;
        let result;
        try {
            pendingRef.value = promise = Promise.resolve(opts.load(params, dataRef.value));
            result = await promise;
            dataRef.value = result;
            dirtyRef.value = false;
            loadedRef.value = true;
            errorRef.value = null;
            if (dataCallback) {
                dataCallback(result);
            }
        }
        catch (error) {
            errorRef.value = error;
            const onError = opts.onError;
            if (!onError) {
                throw error;
            }
            if (effectScope) {
                effectScope.run(() => onError(error, params));
            }
            else {
                onError(error, params);
            }
            return undefined;
        }
        finally {
            // we need to check if this is really the same request we started
            // because in the meantime some other request might start
            if (pendingRef.value === promise) {
                pendingRef.value = null;
            }
        }
        const onLoad = opts.onLoad;
        if (onLoad) {
            if (effectScope) {
                effectScope.run(() => onLoad(result, params));
            }
            else {
                onLoad(result, params);
            }
        }
        return result;
    }
}
function getDebounceOptions(debounceConfig) {
    if (!debounceConfig) {
        return undefined;
    }
    if (typeof debounceConfig === 'number') {
        return { time: debounceConfig, leading: true, trailing: true };
    }
    return {
        time: debounceConfig.time,
        leading: debounceConfig.leading ?? true,
        trailing: debounceConfig.trailing ?? true,
    };
}
//# sourceMappingURL=useDataSource.js.map